import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCart } from '../../contexts/CartContext';
import { ShoppingCart, Plus, Star, Search, ArrowLeft, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';

const MenuView = () => {
  const navigate = useNavigate();
  const { business, categories, products } = useBusiness();
  const { addToCart, getItemCount, deliveryMode, routePrefix } = useCart();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Colores dinámicos
  const primaryColor = business?.primary_color || '#FFD700';
  const bgColor = business?.background_color || '#0D0D0D';
  const cardColor = business?.card_color || '#262626';
  const textColor = business?.text_color || '#FFFFFF';
  const fontHeading = business?.font_heading || 'Playfair Display, serif';
  const fontBody = business?.font_body || 'Roboto, sans-serif';

  // Set first category as selected on mount
  React.useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories]);

  const filteredProducts = React.useMemo(() => {
    let filtered = selectedCategory
      ? products.filter(p => p.category_id === selectedCategory && p.available)
      : products.filter(p => p.available);
    
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [selectedCategory, products, searchQuery]);

  const handleAddToCart = (product, quantity = 1) => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  const handleOpenProduct = (product) => {
    setSelectedProduct(product);
    setProductQuantity(1);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setProductQuantity(1);
  };

  const cartCount = getItemCount();

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: bgColor, color: textColor, fontFamily: fontBody }}>
      {/* Header */}
      <div className="sticky top-0 z-20 border-b" style={{ backgroundColor: bgColor, borderColor: '#333' }}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-md mx-auto mb-3">
            <a 
              href={routePrefix || '/'}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Inicio</span>
            </a>
            <div className="flex-1 text-center">
              <h2 className="text-lg font-bold" style={{ fontFamily: fontHeading, color: primaryColor }}>
                {business.name}
              </h2>
              <p className="text-xs opacity-60">
                {deliveryMode === 'delivery' ? '🚚 Delivery' : '👜 Take Away'}
              </p>
            </div>
            <div className="w-16"></div>
          </div>
          
          {/* Buscador */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 opacity-40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full pl-11 pr-10 py-3 rounded-full text-sm"
              style={{ 
                backgroundColor: cardColor,
                border: `1px solid ${primaryColor}20`,
                color: textColor
              }}
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-5 h-5 opacity-40 hover:opacity-100" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Categories - Horizontal Scroll */}
      <div className="sticky top-[120px] z-10 border-b" style={{ backgroundColor: bgColor, borderColor: '#333' }}>
        <div className="px-4 py-3 overflow-x-auto hide-scrollbar">
          <div className="flex gap-2 max-w-md mx-auto">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="px-5 py-2 rounded-full whitespace-nowrap text-sm font-semibold transition-all"
                style={{
                  backgroundColor: selectedCategory === category.id ? primaryColor : 'transparent',
                  color: selectedCategory === category.id ? bgColor : textColor,
                  border: `2px solid ${selectedCategory === category.id ? primaryColor : primaryColor + '40'}`
                }}
                data-testid={`category-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="px-4 py-6 max-w-md mx-auto">
        <div className="grid grid-cols-1 gap-4">
          {filteredProducts.map((product) => {
            const hasDiscount = product.discount_price && product.discount_price < product.price;
            const finalPrice = hasDiscount ? product.discount_price : product.price;
            
            return (
              <div
                key={product.id}
                className="product-card rounded-2xl overflow-hidden cursor-pointer transition-all hover:scale-[1.02]"
                style={{ backgroundColor: cardColor }}
                onClick={() => handleOpenProduct(product)}
                data-testid={`product-${product.id}`}
              >
                <div className="flex gap-4 p-4">
                  {/* Imagen */}
                  {product.image_url && (
                    <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden relative">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      {product.is_popular && (
                        <div 
                          className="absolute top-1 right-1 rounded-full px-2 py-1 flex items-center gap-1 text-xs font-bold"
                          style={{ backgroundColor: primaryColor, color: bgColor }}
                        >
                          <Star className="w-3 h-3 fill-current" />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-base leading-tight" style={{ color: primaryColor }}>
                          {product.name}
                        </h3>
                        {product.is_popular && !product.image_url && (
                          <Star className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor, fill: primaryColor }} />
                        )}
                      </div>
                      <p className="text-sm opacity-70 line-clamp-2 mt-1">
                        {product.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        {hasDiscount ? (
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-black" style={{ color: primaryColor }}>
                              ${finalPrice.toFixed(0)}
                            </span>
                            <span className="text-sm line-through opacity-50">
                              ${product.price.toFixed(0)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-lg font-black" style={{ color: primaryColor }}>
                            ${finalPrice.toFixed(0)}
                          </span>
                        )}
                        {product.stock === 0 && (
                          <span className="text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full" data-testid={`out-of-stock-${product.id}`}>
                            Agotado
                          </span>
                        )}
                        {product.stock !== null && product.stock > 0 && product.stock <= 5 && (
                          <span className="text-xs text-orange-400 font-medium">
                            Últimas {product.stock}
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.stock !== 0) handleAddToCart(product);
                        }}
                        disabled={product.stock === 0}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${product.stock === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
                        style={{ backgroundColor: product.stock === 0 ? '#666' : primaryColor }}
                        data-testid={`add-to-cart-${product.id}`}
                        title={product.stock === 0 ? 'Sin stock' : 'Agregar al carrito'}
                      >
                        <Plus className="w-5 h-5" style={{ color: bgColor }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12 opacity-50">
            {searchQuery 
              ? 'No se encontraron productos con esa búsqueda'
              : 'No hay productos disponibles en esta categoría'
            }
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 left-0 right-0 px-4 z-30">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => navigate(`${routePrefix}/carrito`)}
              className="w-full h-16 rounded-full text-lg font-bold shadow-2xl flex items-center justify-center gap-3 transition-all hover:scale-105 active:scale-95"
              style={{ backgroundColor: primaryColor, color: bgColor }}
              data-testid="view-cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              Ver Carrito ({cartCount})
            </button>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden" style={{ backgroundColor: cardColor, border: `1px solid ${primaryColor}20` }}>
          {selectedProduct && (
            <>
              {/* Imagen grande */}
              {selectedProduct.image_url && (
                <div className="relative h-64">
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedProduct.is_popular && (
                    <div 
                      className="absolute top-4 right-4 rounded-full px-3 py-2 flex items-center gap-2 text-sm font-bold"
                      style={{ backgroundColor: primaryColor, color: bgColor }}
                    >
                      <Star className="w-4 h-4 fill-current" />
                      Popular
                    </div>
                  )}
                  <button
                    onClick={handleCloseModal}
                    className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              
              <div className="p-6" style={{ color: textColor }}>
                <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: fontHeading, color: primaryColor }}>
                  {selectedProduct.name}
                </h2>
                <p className="opacity-75 mb-6">
                  {selectedProduct.description}
                </p>
                
                {/* Precio y Cantidad */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    {selectedProduct.discount_price && selectedProduct.discount_price < selectedProduct.price ? (
                      <div>
                        <div className="text-3xl font-black" style={{ color: primaryColor }}>
                          ${selectedProduct.discount_price.toFixed(0)}
                        </div>
                        <div className="text-sm line-through opacity-50">
                          ${selectedProduct.price.toFixed(0)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-3xl font-black" style={{ color: primaryColor }}>
                        ${selectedProduct.price.toFixed(0)}
                      </div>
                    )}
                  </div>
                  
                  {/* Selector de cantidad */}
                  <div className="flex items-center gap-3 rounded-full p-1" style={{ backgroundColor: bgColor }}>
                    <button
                      onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all hover:scale-110"
                      style={{ backgroundColor: cardColor }}
                    >
                      -
                    </button>
                    <span className="w-10 text-center font-bold text-lg">
                      {productQuantity}
                    </span>
                    <button
                      onClick={() => setProductQuantity(productQuantity + 1)}
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all hover:scale-110"
                      style={{ backgroundColor: cardColor }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Botón Agregar */}
                <button
                  onClick={() => {
                    handleAddToCart(selectedProduct, productQuantity);
                    handleCloseModal();
                  }}
                  className="w-full h-14 rounded-full text-lg font-bold transition-all hover:scale-105 active:scale-95"
                  style={{ backgroundColor: primaryColor, color: bgColor }}
                  data-testid="add-from-modal"
                >
                  Agregar al Pedido - ${((selectedProduct.discount_price || selectedProduct.price) * productQuantity).toFixed(0)}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuView;
